import { useRef, type ReactNode, type TouchEvent } from "react";

/**
 * The parts the week strip and the month calendar share: the arrows, the swipe, and the date of a
 * day on the Book tab, marked by what a member can do on it.
 */

/** A horizontal swipe longer than this pages the strip or the month. */
const SWIPE_DISTANCE = 40;

/** A swipe to the left is next, to the right is previous. A scroll down the page never pages. */
export function useSwipe({
  onPrevious,
  onNext,
  canPrevious,
  canNext,
}: {
  onPrevious: () => void;
  onNext: () => void;
  canPrevious: boolean;
  canNext: boolean;
}) {
  const start = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    start.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };
  const onTouchEnd = (event: TouchEvent) => {
    const from = start.current;
    const touch = event.changedTouches[0];
    start.current = null;
    if (!from || !touch) return;
    const dx = touch.clientX - from.x;
    // Mostly sideways, so a scroll down the page never changes the week.
    if (Math.abs(dx) < SWIPE_DISTANCE || Math.abs(dx) < Math.abs(touch.clientY - from.y) * 1.5) return;
    if (dx < 0 && canNext) onNext();
    else if (dx > 0 && canPrevious) onPrevious();
  };
  return { onTouchStart, onTouchEnd };
}

export function CalendarArrow({
  label,
  onClick,
  disabled = false,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="flex size-11 shrink-0 items-center justify-center rounded-xl text-ink-2 transition-colors duration-150 active:bg-sunken disabled:opacity-35 disabled:pointer-events-none"
    >
      {children}
    </button>
  );
}

/**
 * What a member can do on a day: book (`open`, a session with a free spot), only look (`closed`,
 * sessions that are full or started), or nothing (`none`).
 */
export type DayAvailability = "open" | "closed" | "none";

/**
 * The date of a Book tab day. An open day is a filled blue disc, a closed day a dashed ring, a day
 * with nothing on it the bare number. The shape carries the meaning, never the colour alone, and
 * the day's label says it in words. On the blue tile of the picked day the open disc turns white.
 */
export function AvailabilityDate({
  availability,
  onBlue,
  isToday,
  children,
}: {
  availability: DayAvailability;
  onBlue: boolean;
  isToday: boolean;
  children: ReactNode;
}) {
  const look =
    availability === "open"
      ? onBlue
        ? "bg-inverse text-accent-strong"
        : "bg-accent-surface text-accent-strong ring-1 ring-inset ring-accent-soft"
      : availability === "closed"
        ? onBlue
          ? "border-[1.5px] border-dashed border-inverse/85"
          : "border-[1.5px] border-dashed border-line-strong text-ink-2"
        : onBlue
          ? ""
          : "text-ink-2";
  return (
    <span
      data-availability={availability}
      className={[
        // As wide as the day's column, 36 px at most, so a disc never reaches into the next day.
        "flex aspect-square w-full max-w-9 items-center justify-center rounded-full",
        "text-[15px] leading-none font-semibold tabular-nums",
        look,
        // Today keeps a blue ring when another day is picked, so the member never loses today.
        isToday && !onBlue ? "outline-2 outline-offset-1 outline-accent" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </span>
  );
}

/** The small count under a Book tab date: open sessions in blue, closed ones in grey, nothing empty. */
export function AvailabilityCount({ availability, count, onBlue }: { availability: DayAvailability; count: number; onBlue: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={[
        "h-3.5 text-[10px] leading-3.5 font-semibold tabular-nums",
        onBlue ? "text-inverse/85" : availability === "open" ? "text-accent" : "text-ink-3",
      ].join(" ")}
    >
      {availability === "none" ? "" : count}
    </span>
  );
}
